import { LoadingOutlined } from '@ant-design/icons';
import { Spin, Tag } from 'antd';
import React from 'react';
import styled, { css } from 'styled-components';

import PlusSvg from '../../../assets/icons/PlusSvg';
import theme from '../../../assets/theme';
import { useCroppedImage } from '../../../hooks/useCroppedImage';
import { useTextFile } from '../../../hooks/useGameMediaBackgroundJSONFile';
import { useGameMediaFile } from '../../../hooks/useGameMediaFile';
import useSvgImage from '../../../hooks/useSvgImage';
import { CenteredContainer } from '../../../styles/CenteredContainer';
import { CropConfig } from '../../../utils/imageCropUtils';
import { getExtensionFromMimeType } from '../../../utils/mimetype';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { MarkdownSlateViewer } from '../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';

export const AgentImageRenderer = ({
  fileId,
  fileMimetype,
  onClickAddToSlide,
  objectFit,
  brightness,
  fileAltText,
  style,
}: {
  fileId: string;
  fileMimetype: string;
  onClickAddToSlide: () => void;
  objectFit?: string;
  brightness?: number | null;
  fileAltText?: string | null;
  style?: React.CSSProperties;
}) => {
  const { imageUrl, isLoading } = useCroppedImage({
    fileId,
    cropConfig: undefined,
    fileMimetype,
    isDiscoverTemplate: false,
    isAuthenticated: true,
    isOwner: true,
  });

  if (isLoading && !imageUrl) {
    return (
      <CenteredContainer style={{ height: '100%', width: '100%' }}>
        <Spin
          style={{ color: theme.colors.orange }}
          indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
        />
      </CenteredContainer>
    );
  }

  return (
    <div style={{ position: 'relative', padding: '0px 20px', marginBottom: 20 }}>
      <ImgStyled
        $objectFit={objectFit}
        $brightness={brightness}
        src={imageUrl}
        alt={fileAltText || ''}
        style={style}
      />
      <ImageActionsContainer>
        <ImageActionButton
          onClick={() => {
            onClickAddToSlide();
          }}
        >
          <IconWrapper height="14px" width="14px">
            <PlusSvg />
          </IconWrapper>
          <span>Add to slide</span>
        </ImageActionButton>
        {/* <ImageActionButton $disabled={true}>
          <IconWrapper>
            <RefreshSvg />
          </IconWrapper>
          <span>Make drawing background</span>
        </ImageActionButton> */}
      </ImageActionsContainer>
    </div>
  );
};

const ImageActionsContainer = styled.div`
  position: absolute;
  height: 28px;
  bottom: -14px;
  right: 5px;
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  /* gap: 3px; */
  border-radius: 8px;
  border: 1px solid #a8a8a8;
  background-color: ${theme.colors.greyLight};
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  padding: 3px 3px;
`;

const ImageActionButton = styled.div<{ $disabled?: boolean }>`
  display: grid;
  align-items: center;
  height: 100%;
  grid-template-columns: 16px 1fr;
  gap: 3px;
  border-radius: 5px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};

  font-size: 10px;
  font-weight: bold;

  transition: all 0.2s ease;
  padding: 0px 5px;

  &:hover {
    background-color: ${theme.colors.grey};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      &:hover {
        background-color: transparent;
      }
    `}
`;

const CroppedGameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
  onLoad,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const { imageUrl, isLoading } = useCroppedImage({
    fileId,
    cropConfig,
    fileMimetype,
    isDiscoverTemplate,
    isAuthenticated,
    isOwner,
  });

  if (isLoading && !imageUrl) {
    return (
      <CenteredContainer style={{ height: '100%', width: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <>
      <ImgStyled
        onLoad={e => {
          onLoad?.(e);
        }}
        $objectFit={objectFit}
        $brightness={brightness}
        src={imageUrl}
        alt={fileAltText || ''}
        onClick={() => onImageClicked && imageUrl && onImageClicked(imageUrl)}
        $clickable={!!onImageClicked}
        style={style}
      />
    </>
  );
};

export const TextFileRenderer = ({ fileId }: { fileId: string }) => {
  const { data } = useTextFile(fileId);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        padding: '4px 6px',
        backgroundColor: theme.colors.paleBeige,
      }}
    >
      <p style={{ fontSize: 9, textAlign: 'left', width: '100%' }}>{data}</p>
      <BottomBlur />
    </div>
  );
};

export const OtherFileRenderer = ({
  fileId,
  fileMimetype,
}: {
  fileId: string;
  fileMimetype: string;
}) => {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        padding: '4px 6px',
        backgroundColor: theme.colors.paleBeige,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 4,
        justifyContent: 'space-between',
        fontSize: 9,
      }}
    >
      <p
        style={{
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflowWrap: 'anywhere',
          wordBreak: 'break-all',
          textAlign: 'left',
        }}
      >
        {fileId}
      </p>
      <Tag>{getExtensionFromMimeType(fileMimetype)}</Tag>
    </div>
  );
};
export const TextFileRendererLargePreview = ({ fileId }: { fileId: string }) => {
  const { data } = useTextFile(fileId);

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        overflowY: 'auto',
        padding: '8px 10px',
      }}
    >
      <MarkdownSlateViewer content={data || ''} />
    </div>
  );
};

const BottomBlur = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(255, 255, 255, 1), transparent);
`;

const SvgFileRenderer = ({
  fileId,
  isAuthenticated,
  fileAltText,
  isOwner,
}: {
  fileId: string;
  isAuthenticated: boolean;
  fileAltText?: string | null;
  isOwner: boolean;
}) => {
  const { svgText, isLoading } = useSvgImage({
    fileId,
    fileMimetype: 'image/svg+xml',
    isDiscoverTemplate: false,
    isAuthenticated: isAuthenticated,
    isOwner: isOwner,
    colors: [],
  });

  if (isLoading && !svgText) {
    return (
      <CenteredContainer style={{ height: '100%', width: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <SvgImg
      aria-label={fileAltText || ''}
      dangerouslySetInnerHTML={{ __html: svgText }}
    />
  );
};

const GifFileRenderer = ({
  fileId,
  isAuthenticated,
  objectFit,
  brightness,
  fileAltText,
  style,
  onLoad,
}: {
  fileId: string;
  isAuthenticated: boolean;
  objectFit?: string;
  brightness?: number | null;
  fileAltText?: string | null;
  style?: React.CSSProperties;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const { data, isLoading } = useGameMediaFile(fileId, isAuthenticated);

  if (isLoading && !data) {
    return (
      <CenteredContainer style={{ height: '100%' }}>
        <Spin />
      </CenteredContainer>
    );
  }

  return (
    <ImgStyled
      $objectFit={objectFit}
      $brightness={brightness}
      src={data}
      alt={fileAltText || ''}
      $clickable={false}
      style={style}
      onLoad={e => {
        onLoad?.(e);
      }}
    />
  );
};

const GameImageRenderer = ({
  fileId,
  fileAltText,
  cropConfig,
  fileMimetype,
  isDiscoverTemplate,
  objectFit,
  onImageClicked,
  style,
  brightness,
  isAuthenticated,
  isOwner,
  onLoad,
}: {
  fileId: string;
  fileMimetype: string;
  fileAltText?: string | null;
  cropConfig?: CropConfig | null;
  isDiscoverTemplate: boolean;
  objectFit?: string;
  brightness?: number | null;
  onImageClicked?: (url: string) => void;
  style?: React.CSSProperties;
  isAuthenticated: boolean;
  isOwner: boolean;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  // @depricated by new mediaUrl
  if (fileMimetype === 'image/gif') {
    return (
      <GifFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        brightness={brightness}
        isAuthenticated={isAuthenticated}
        objectFit={objectFit}
        style={style}
        onLoad={onLoad}
      />
    );
  }

  if (fileId.includes('.svg')) {
    return (
      <SvgFileRenderer
        fileAltText={fileAltText}
        fileId={fileId}
        isAuthenticated={isAuthenticated}
        isOwner={isOwner}
      />
    );
  }

  return (
    <CroppedGameImageRenderer
      fileAltText={fileAltText}
      fileId={fileId}
      fileMimetype={fileMimetype}
      brightness={brightness}
      cropConfig={cropConfig}
      isDiscoverTemplate={isDiscoverTemplate}
      isAuthenticated={isAuthenticated}
      objectFit={objectFit}
      onImageClicked={onImageClicked}
      style={style}
      isOwner={isOwner}
      onLoad={onLoad}
    />
  );
};

export default GameImageRenderer;

export const ImgStyled = styled.img<{
  $objectFit?: string;
  $clickable?: boolean;
  $brightness?: number | null;
}>`
  width: 100%;
  height: 100%;
  filter: ${({ $brightness }) =>
    /** 0.5 is no change in brightness, but needs to be translated into the value of 1 for CSS */

    $brightness && `brightness(${($brightness || 0.5) + 0.5})`};

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
  ${({ $objectFit = 'contain' }) =>
    $objectFit &&
    css`
      object-fit: ${$objectFit};
    `}
`;
const SvgImg = styled.div`
  width: 100%;
  height: 100%;
  svg {
    width: 100%;
    height: auto;
  }
`;
